import React, { useState } from "react";
import FoodInput from "./components/FoodInput";
import PlanDisplay from "./components/PlanDisplay";
import Navbar from "./components/Navbar";
import { findFoodsInInput } from "./utils/foodMatcher";
import aiService from "./services/aiService";
import { savePlan, getPlans } from "./services/firebase";
import "./App.css";

function App() {
  const [foods, setFoods] = useState([]);
  const [mealPlan, setMealPlan] = useState(null);
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [showPlansModal, setShowPlansModal] = useState(false);

  const handleGeneratePlan = async (userInput, options) => {
    setLoading(true);
    setError(null);

    try {
      // Find foods in user input
      const matchedFoods = findFoodsInInput(userInput);

      if (matchedFoods.length === 0) {
        setError(
          "No recognized foods found. Please try: chicken, rice, eggs, broccoli, etc."
        );
        setLoading(false);
        return;
      }

      setFoods(matchedFoods);

      // Generate meal plan using AI service with protein target
      const generatedMealPlan = await aiService.generateMealPlan(matchedFoods, {
        ...options,
        protein: options.protein || 150,
      });
      setMealPlan(generatedMealPlan);

      // Generate workout plan
      const generatedWorkoutPlan = await aiService.generateWorkoutPlan(options);
      setWorkoutPlan(generatedWorkoutPlan);
    } catch (err) {
      console.error("Error generating plan:", err);
      setError("Failed to generate plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async () => {
    if (!mealPlan || !workoutPlan) {
      alert("No plan to save. Please generate a plan first.");
      return;
    }

    try {
      const planData = {
        mealPlan,
        workoutPlan,
        foods,
        createdAt: new Date().toISOString(),
      };

      await savePlan(planData);
      alert("Plan saved successfully!");
    } catch (err) {
      console.error("Error saving plan:", err);
      alert("Failed to save plan. Check console for details.");
    }
  };

  const handleLoadPlans = async () => {
    try {
      const plans = await getPlans();
      setSavedPlans(plans);
      setShowPlansModal(true);
    } catch (err) {
      console.error("Error loading plans:", err);
      alert("Failed to load saved plans.");
    }
  };

  const handleVoiceInput = (transcript) => {
    console.log("Voice input received:", transcript);
  };

  return (
    <div className="App">
      <Navbar onSavePlan={handleSavePlan} onLoadPlans={handleLoadPlans} />

      <div className="app-content">
        {!mealPlan && (
          <FoodInput
            onSubmit={handleGeneratePlan}
            onVoiceInput={handleVoiceInput}
          />
        )}

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Generating your personalized 7-day plan...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">⚠️ {error}</p>
            <button
              onClick={() => setError(null)}
              className="close-error-button"
            >
              Dismiss
            </button>
          </div>
        )}

        {mealPlan && workoutPlan && !loading && (
          <>
            <button
              onClick={() => {
                setMealPlan(null);
                setWorkoutPlan(null);
                setFoods([]);
                setError(null);
              }}
              className="new-plan-button"
            >
              ← Create New Plan
            </button>
            <PlanDisplay
              mealPlan={mealPlan}
              workoutPlan={workoutPlan}
              foods={foods}
            />
          </>
        )}
      </div>

      {/* Saved Plans Modal */}
      {showPlansModal && (
        <div className="modal-overlay" onClick={() => setShowPlansModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📋 Saved Plans</h2>
            {savedPlans.length === 0 ? (
              <p>No saved plans yet.</p>
            ) : (
              <ul className="plans-list">
                {savedPlans.map((plan) => (
                  <li key={plan.id}>
                    <h3>{plan.mealPlan.goal} Plan</h3>
                    <p>{new Date(plan.createdAt).toLocaleDateString()}</p>
                    <button
                      onClick={() => {
                        setMealPlan(plan.mealPlan);
                        setWorkoutPlan(plan.workoutPlan);
                        setFoods(plan.foods);
                        setShowPlansModal(false);
                      }}
                    >
                      Load
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => setShowPlansModal(false)}
              className="close-modal"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
